import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';
import { CartDiscountUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = ['cartDiscountError', 'cartDiscountErrorDiscountCode', 'cartDiscountErrorShipping'];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** Cancels any request still in flight and starts a new one. */
  #createAbortController() {
    this.#activeFetch?.abort();

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  /**
   * Sends the full list of discount codes to the cart and returns the response.
   * @param {string} sectionId - The cart section to re-render.
   * @param {string[]} codes - Every discount code the cart should have.
   * @param {AbortSignal} signal - Cancels the request.
   */
  async #updateCart(sectionId, codes, signal) {
    const config = fetchConfig('json', {
      body: JSON.stringify({ discount: codes.join(','), sections: [sectionId] }),
    });
    const response = await fetch(Theme.routes.cart_update_url, { ...config, signal });

    return response.json();
  }

  /**
   * @param {string} sectionId - The section to re-render.
   * @param {string} html - The new section HTML.
   */
  #morph(sectionId, html) {
    morphSection(sectionId, html, { mode: this.closest('theme-drawer') ? 'hydration' : 'full' });
  }

  /**
   * Reports a failed request, ignoring requests we cancelled ourselves.
   * @param {unknown} error - The error thrown by the request.
   * @param {string} fallback - The message to use when the error has none.
   */
  #reportError(error, fallback) {
    if (!(error instanceof Error) || error.name === 'AbortError') return;

    this.dispatchEvent(new CartErrorEvent({ error: error.message || fallback, code: 'SERVICE_UNAVAILABLE' }));
  }

  /**
   * Handles applying a discount code from our form.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const input = form.querySelector('input[name="discount"]');
    const sectionId = this.dataset.sectionId;
    if (!(input instanceof HTMLInputElement) || typeof sectionId !== 'string') return;

    const code = input.value;
    const existing = this.#existingDiscounts();
    if (existing.includes(code)) return;

    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    cartDiscountError.classList.add('hidden');
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');

    const allCodes = [...existing, code];
    const abortController = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: allCodes.map((value) => ({ code: value })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const data = await this.#updateCart(sectionId, allCodes, abortController.signal);
      const cart = CartDiscountUpdateEvent.createCartFromAjaxResponse(data);

      /** @param {boolean} applicable */
      const codeIs = (applicable) =>
        data.discount_codes.some(
          (/** @type {{ code: string; applicable: boolean; }} */ discount) =>
            discount.code === code && discount.applicable === applicable
        );

      if (codeIs(false)) {
        input.value = '';
        this.#handleDiscountError('discount_code');
        deferredPromise.resolve({ cart });
        return;
      }

      const newHtml = data.sections[sectionId];
      const section = new DOMParser()
        .parseFromString(newHtml, 'text/html')
        .getElementById(`shopify-section-${sectionId}`);

      if (section) {
        // Before morphing, check whether the code was a shipping discount that the UI won't show:
        // the pills rendered by liquid are unchanged, but the cart says the code is applicable.
        const renderedCodes = Array.from(section.querySelectorAll('.cart-discount__pill'))
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);

        if (
          renderedCodes.length === existing.length &&
          renderedCodes.every((renderedCode) => existing.includes(/** @type {string} */ (renderedCode))) &&
          codeIs(true)
        ) {
          this.#handleDiscountError('shipping');
          input.value = '';
          deferredPromise.resolve({ cart });
          return;
        }
      }

      deferredPromise.resolve({ cart });
      // data-skip-node-update on <input name="discount"> stops morphSection from clearing the field,
      // so clear it here or the typed code stays after a successful apply.
      input.value = '';
      this.#morph(sectionId, newHtml);
    } catch (error) {
      deferredPromise.reject(error);
      this.#reportError(error, 'Failed to apply discount');
    } finally {
      // Only clear our own request, a newer one may have replaced it
      if (this.#activeFetch === abortController) this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    // Only clicks and the Enter key remove a discount. Leave every other key alone.
    if (event instanceof KeyboardEvent && event.key !== 'Enter') return;

    event.preventDefault();
    event.stopPropagation();

    const sectionId = this.dataset.sectionId;
    if (!(event.target instanceof HTMLElement) || typeof sectionId !== 'string') return;

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const code = pill.dataset.discountCode;
    if (!code) return;

    const remaining = this.#existingDiscounts();
    const index = remaining.indexOf(code);
    if (index === -1) return;

    remaining.splice(index, 1);

    const abortController = this.#createAbortController();
    const deferredPromise = CartDiscountUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartDiscountUpdateEvent({
        discountCodes: remaining.map((value) => ({ code: value })),
        promise: deferredPromise.promise,
      })
    );

    try {
      const data = await this.#updateCart(sectionId, remaining, abortController.signal);

      deferredPromise.resolve({ cart: CartDiscountUpdateEvent.createCartFromAjaxResponse(data) });
      this.#morph(sectionId, data.sections[sectionId]);
    } catch (error) {
      deferredPromise.reject(error);
      this.#reportError(error, 'Failed to remove discount');
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'shipping'} type - The type of discount error.
   */
  #handleDiscountError(type) {
    const { cartDiscountError, cartDiscountErrorDiscountCode, cartDiscountErrorShipping } = this.refs;
    const target = type === 'discount_code' ? cartDiscountErrorDiscountCode : cartDiscountErrorShipping;
    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: type === 'discount_code' ? 'Invalid discount code' : 'Discount not applicable for shipping',
        code: 'VALIDATION_CUSTOM',
      })
    );
  }

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];

    for (const pill of this.querySelectorAll('.cart-discount__pill')) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
